<template>
    <div class="main" style="padding-bottom: 2rem;">

        <!-- Loading FD / initializing -->
        <div v-if="!fd || isInitializing" class="has-text-centered mt-6">
            <base-spinner></base-spinner>
        </div>

        <div v-else>
            <!-- FD header (shown on all steps except success) -->
            <div v-if="step < 7" class="fd-header">
                <p class="fd-header__date">{{ formatDate(fd.date) }}</p>
                <p v-if="fd.sponsor" class="fd-header__sponsor">{{ fd.sponsor }}</p>
            </div>

            <!-- Step progress indicator -->
            <div v-if="step >= 1 && step < 7" class="wizard-stepper">
                <template v-for="(stage, idx) in stepperStages" :key="stage.label">
                    <div class="wizard-stepper__item"
                         :class="{ 'is-active': currentStageIndex === idx, 'is-done': currentStageIndex > idx }">
                        <div class="wizard-stepper__dot">
                            <i v-if="currentStageIndex > idx" class="fas fa-check"></i>
                            <span v-else>{{ idx + 1 }}</span>
                        </div>
                        <p class="wizard-stepper__label">{{ stage.label }}</p>
                    </div>
                    <div v-if="idx < stepperStages.length - 1"
                         class="wizard-stepper__line"
                         :class="{ 'is-done': currentStageIndex > idx }">
                    </div>
                </template>
            </div>

            <!-- Error banner -->
            <div v-if="error" class="notification is-danger is-light">
                <p>{{ error }}</p>
            </div>

            <!-- Step 1: Sponsor or Regular -->
            <div v-if="step === 1" class="wizard-step">
                <p class="wizard-step__question">Are you volunteering with <strong>{{ fd.sponsor }}</strong>?</p>
                <button class="wizard-choice-btn is-primary-choice" @click="selectVolunteerType('sponsor')">
                    ✅ Yes — I'm with "{{ fd.sponsor }}"
                </button>
                <button class="wizard-choice-btn is-secondary-choice" @click="selectVolunteerType('regular')">
                    🙋 No — I'm a regular volunteer
                </button>
            </div>

            <!-- Step 2: Location -->
            <div v-if="step === 2" class="wizard-step">
                <p class="wizard-step__question">Which location are you signing up for?</p>

                <div v-if="!redhillAvailable && !tbrAvailable" class="notification is-warning has-text-centered">
                    <p>All volunteer spots for this FD are filled.</p>
                    <p class="mt-2">See you next time! 😊</p>
                </div>

                <div v-else>
                    <div class="location-card" :class="{ 'is-disabled': !redhillAvailable }" @click="redhillAvailable && selectLocation('REDHILL')">
                        <div class="location-card__icon">📍</div>
                        <div class="location-card__body">
                            <p class="location-card__name">Redhill</p>
                            <p class="location-card__address">
                                89 Redhill Close ·
                                <a href="https://maps.google.com/?q=89+Redhill+Close+Singapore"
                                   target="_blank" rel="noopener"
                                   class="location-card__map-link"
                                   @click.stop>Open in Maps</a>
                            </p>
                            <p class="location-card__detail">Driving &amp; Walking · starts {{ fd.time }}</p>
                        </div>
                        <div class="location-card__badge" :class="redhillAvailable ? 'is-available' : 'is-full'">
                            {{ redhillAvailable ? 'Open' : 'Full' }}
                        </div>
                    </div>

                    <div class="location-card mt-3" :class="{ 'is-disabled': !tbrAvailable }" @click="tbrAvailable && selectLocation('TBR')">
                        <div class="location-card__icon">📍</div>
                        <div class="location-card__body">
                            <p class="location-card__name">TBR</p>
                            <p class="location-card__address">
                                32 Telok Blangah Rise ·
                                <a href="https://maps.google.com/?q=32+Telok+Blangah+Rise+Singapore"
                                   target="_blank" rel="noopener"
                                   class="location-card__map-link"
                                   @click.stop>Open in Maps</a>
                            </p>
                            <p class="location-card__detail">Walking only · starts {{ tbrTime }}</p>
                        </div>
                        <div class="location-card__badge" :class="tbrAvailable ? 'is-available' : 'is-full'">
                            {{ tbrAvailable ? 'Open' : (tbrRequired ? 'Full' : 'Not needed') }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Step 3: Role (Redhill only) -->
            <div v-if="step === 3" class="wizard-step">
                <p class="wizard-step__question">Can you do deliveries by car?</p>

                <div class="driver-option">
                    <button
                        class="wizard-choice-btn"
                        :class="redhillDriversAvailable ? 'is-primary-choice' : 'is-choice-full'"
                        :disabled="!redhillDriversAvailable"
                        @click="redhillDriversAvailable && selectRole('DRIVER')">
                        <span class="role-btn-content">
                            <span><i class="fas fa-car mr-2"></i> Yes, I will come with a car</span>
                            <span v-if="!redhillDriversAvailable" class="choice-full-tag">Full</span>
                        </span>
                    </button>
                    <p v-if="!redhillDriversAvailable && redhillWalkersAvailable" class="role-fallback-hint">
                        You can still sign-up as a walking volunteer 🙂
                    </p>
                    <p v-if="redhillDriversAvailable" class="role-info-toggle" @click="showDriverInfo = !showDriverInfo">
                        <i class="fas fa-circle-info"></i> {{ showDriverInfo ? 'Hide info' : 'What does this mean?' }}
                    </p>
                    <div v-if="showDriverInfo && redhillDriversAvailable" class="role-info-box">
                        You will be helping with the driving deliveries which needs a car.
                    </div>
                </div>

                <button
                    class="wizard-choice-btn"
                    :class="redhillWalkersAvailable ? 'is-secondary-choice' : 'is-choice-full'"
                    :disabled="!redhillWalkersAvailable"
                    @click="redhillWalkersAvailable && selectRole('OTHER')">
                    <span class="role-btn-content">
                        <span><i class="fas fa-person-walking mr-2"></i> No, I will do walking deliveries</span>
                        <span v-if="!redhillWalkersAvailable" class="choice-full-tag">Full</span>
                    </span>
                </button>

                <div v-if="!redhillDriversAvailable && !redhillWalkersAvailable" class="notification is-warning mt-3">
                    <p>No spots left at Redhill. Please go back and choose TBR.</p>
                </div>
            </div>

            <!-- Step 4: +1 -->
            <div v-if="step === 4" class="wizard-step">
                <p class="wizard-step__question">Are you bringing someone with you?</p>
                <button class="wizard-choice-btn is-primary-choice" @click="selectPlusOne(true)">
                    Yes — I'm bringing a +1
                </button>
                <button class="wizard-choice-btn is-secondary-choice" @click="selectPlusOne(false)">
                    No — just me
                </button>
            </div>

            <!-- Step 5: Auth gate -->
            <div v-if="step === 5">
                <VolunteerInlineAuth @authenticated="onAuthenticated" />
            </div>

            <!-- Step 6: Confirm + T&C -->
            <div v-if="step === 6" class="wizard-step">
                <p class="wizard-step__question wizard-step__question--sm">Ready to confirm?</p>

                <div class="confirm-summary box">
                    <div class="confirm-summary__row">
                        <span class="confirm-summary__label">FD Date</span>
                        <span>{{ formatDate(fd.date) }}</span>
                    </div>
                    <div class="confirm-summary__row">
                        <span class="confirm-summary__label">Location</span>
                        <span>{{ location }}</span>
                    </div>
                    <div class="confirm-summary__row">
                        <span class="confirm-summary__label">Role</span>
                        <span>{{ role === 'DRIVER' ? 'Driving' : 'Walking' }}</span>
                    </div>
                    <div class="confirm-summary__row">
                        <span class="confirm-summary__label">Volunteer type</span>
                        <span>{{ volunteerType === 'sponsor' ? fd.sponsor : 'Regular' }}</span>
                    </div>
                    <div class="confirm-summary__row">
                        <span class="confirm-summary__label">Bringing a +1</span>
                        <span>{{ hasPlusOne ? 'Yes' : 'No' }}</span>
                    </div>
                </div>

                <!-- Phone number -->
                <div class="field mt-3">
                    <label class="label phone-label">
                        Mobile number
                        <span v-if="!phoneHasValue" class="phone-required-tag">Required</span>
                        <span v-else class="phone-optional-tag">Optional — update if needed</span>
                    </label>
                    <div class="field has-addons phone-addons">
                        <div class="control">
                            <div class="select" :class="{ 'is-danger': phoneError }">
                                <select v-model="phoneCountryCode" @change="phoneError = null">
                                    <option value="+65">🇸🇬 +65</option>
                                    <option value="+60">🇲🇾 +60</option>
                                    <option value="+91">🇮🇳 +91</option>
                                    <option value="+61">🇦🇺 +61</option>
                                    <option value="+44">🇬🇧 +44</option>
                                    <option value="+1">🇺🇸 +1</option>
                                </select>
                            </div>
                        </div>
                        <div class="control is-expanded has-icons-left">
                            <input
                                v-model="phoneInput"
                                class="input"
                                :class="{ 'is-danger': phoneError }"
                                type="tel"
                                inputmode="numeric"
                                placeholder="91234567"
                                @input="phoneError = null"
                            >
                            <span class="icon is-small is-left">
                                <i class="fas fa-mobile-alt"></i>
                            </span>
                        </div>
                    </div>
                    <p v-if="phoneError" class="help is-danger">{{ phoneError }}</p>
                    <p v-else-if="!phoneHasValue" class="help">We use this to contact you on the day if needed.</p>
                </div>

                <div class="field mt-3">
                    <label class="checkbox tnc-label">
                        <input type="checkbox" v-model="acceptedTnC" class="mr-2">
                        I accept the <a class="tnc-link" @click.prevent="showTnC = true">Terms &amp; Conditions</a>
                    </label>
                </div>

                <button class="button is-info is-rounded is-fullwidth is-large mt-3"
                    :class="{ 'is-loading': isLoading }"
                    :disabled="!acceptedTnC || isLoading"
                    @click="submitSignUp">
                    Register as Volunteer
                </button>
            </div>

            <!-- Step 7: Success -->
            <div v-if="step === 7">
                <div class="success-hero">
                    <div class="success-hero__emoji">🎉</div>
                    <div>
                        <p class="success-hero__title">You are registered. Thanks!</p>
                        <p class="success-hero__subtitle">See you at Food Distribution, {{ currentUserName }}.</p>
                    </div>
                </div>

                <div class="info-card">
                    <div class="info-card__rows">
                        <div class="info-row">
                            <span class="info-row__icon">📍</span>
                            <span>{{ locationAddress }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-row__icon">📅</span>
                            <span>{{ formatDayOfWeek(fd.date) }}, {{ formatDate(fd.date) }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-row__icon">🕐</span>
                            <span><b>{{ locationTime }}</b> hours</span>
                        </div>
                    </div>
                    <div class="success-actions">
                        <button class="button is-rounded calendar-btn" @click="addToCalendar">
                            <span class="icon"><i class="fas fa-calendar-plus"></i></span>
                            <span>Add to calendar</span>
                        </button>
                        <button class="button is-rounded share-btn" @click="shareSignUpLink">
                            <span class="icon"><i class="fas fa-share-nodes"></i></span>
                            <span>Share sign-up link</span>
                        </button>
                        <a href="https://wa.me/6596140431"
                           target="_blank" rel="noopener"
                           class="button is-rounded contact-btn">
                            <span class="icon"><i class="fab fa-whatsapp"></i></span>
                            <span>Contact us</span>
                        </a>
                    </div>
                </div>

                <OfferHelpBanner />
            </div>

            <!-- Back button (steps 2-4, 6) -->
            <div v-if="step >= 2 && step <= 4 || step === 6" class="has-text-centered mt-4">
                <a class="has-text-grey is-size-7" @click="goBack">← Back</a>
            </div>
        </div>

        <!-- T&C Modal -->
        <div class="modal" :class="{ 'is-active': showTnC }">
            <div class="modal-background" @click="showTnC = false"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">Terms &amp; Conditions</p>
                    <button class="delete" aria-label="close" @click="showTnC = false"></button>
                </header>
                <section class="modal-card-body">
                    <p class="tnc-section-title">Photography &amp; Media Consent</p>
                    <p class="tnc-body">
                        By signing up to volunteer, you consent to Apex Club of Bukit Timah photographing and/or
                        recording video during the Food Distribution event. These images and videos may be used
                        on our official social media platforms (including Instagram, Facebook, and LinkedIn) and
                        other communications to promote our community programmes.
                    </p>
                    <p class="tnc-body mt-3">
                        If you do not wish to be photographed or filmed, please inform a PIC (Person in Charge) on the day
                        and we will do our best to accommodate your request.
                    </p>
                </section>
                <footer class="modal-card-foot">
                    <button class="button is-info is-rounded" @click="showTnC = false">Got it</button>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import { auth } from '@/firebase/firebaseInit';
import VolunteerInlineAuth from '@/components/volunteer/VolunteerInlineAuth.vue';
import OfferHelpBanner from '@/components/volunteer/OfferHelpBanner.vue';

export default {
    components: { VolunteerInlineAuth, OfferHelpBanner },
    props: {
        fdId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            step: 1,
            volunteerType: null,
            location: null,
            role: null,
            hasPlusOne: null,
            acceptedTnC: false,
            isLoading: false,
            error: null,
            stepHistory: [],
            isLoggedInAtStart: false,
            showDriverInfo: false,
            showTnC: false,
            isInitializing: true,
            phoneCountryCode: '+65',
            phoneInput: '',
            phoneError: null
        }
    },
    computed: {
        fd() {
            return this.$store.getters['fd/getCurrentFD'];
        },
        currentUser() {
            return this.$store.getters['user/getCurrentUser'];
        },
        currentUserName() {
            return (this.currentUser && this.currentUser.name) || (auth.currentUser && auth.currentUser.displayName) || 'friend';
        },
        tbrTime() {
            if (!this.fd || !this.fd.time) return '--:--';
            const [hours, minutes] = this.fd.time.split(':').map(Number);
            const total = hours * 60 + minutes + 45;
            const h = String(Math.floor(total / 60) % 24).padStart(2, '0');
            const m = String(total % 60).padStart(2, '0');
            return `${h}:${m}`;
        },
        redhillAvailable() {
            if (!this.fd) return false;
            if (this.volunteerType === 'sponsor') {
                return (this.fd.sponsor_volunteers_drivers_remaining > 0) || (this.fd.sponsor_volunteers_others_remaining > 0);
            }
            return (this.fd.volunteers_drivers_remaining > 0) || (this.fd.volunteers_others_remaining > 0);
        },
        tbrAvailable() {
            if (!this.fd) return false;
            if (this.volunteerType === 'sponsor') {
                return (this.fd.sponsor_tbr_volunteers_required > 0) && (this.fd.sponsor_tbr_volunteers_remaining > 0);
            }
            return (this.fd.tbr_volunteers_required > 0) && (this.fd.tbr_volunteers_remaining > 0);
        },
        tbrRequired() {
            if (!this.fd) return false;
            return this.volunteerType === 'sponsor'
                ? this.fd.sponsor_tbr_volunteers_required > 0
                : this.fd.tbr_volunteers_required > 0;
        },
        redhillDriversAvailable() {
            if (!this.fd) return false;
            return this.volunteerType === 'sponsor'
                ? this.fd.sponsor_volunteers_drivers_remaining > 0
                : this.fd.volunteers_drivers_remaining > 0;
        },
        redhillWalkersAvailable() {
            if (!this.fd) return false;
            return this.volunteerType === 'sponsor'
                ? this.fd.sponsor_volunteers_others_remaining > 0
                : this.fd.volunteers_others_remaining > 0;
        },
        stepperStages() {
            const stages = [];
            if (this.fd && this.fd.sponsor) {
                stages.push({ label: 'Sponsor', wizardSteps: [1] });
            }
            stages.push({ label: 'Location', wizardSteps: [2] });
            if (this.location === 'REDHILL') {
                stages.push({ label: 'Car/Walk', wizardSteps: [3] });
            }
            stages.push({ label: '+1', wizardSteps: [4] });
            if (!this.isLoggedInAtStart) {
                stages.push({ label: 'Account', wizardSteps: [5] });
            }
            stages.push({ label: 'Confirm', wizardSteps: [6] });
            return stages;
        },
        currentStageIndex() {
            return this.stepperStages.findIndex(s => s.wizardSteps.includes(this.step));
        },
        locationAddress() {
            return this.location === 'TBR' ? '32 Telok Blangah Rise' : 'Void Deck, 89 Redhill Close';
        },
        locationTime() {
            return this.location === 'TBR' ? this.tbrTime : (this.fd ? this.fd.time : '');
        },
        storedPhone() {
            return (this.currentUser && this.currentUser.phone) ? String(this.currentUser.phone) : '';
        },
        phoneHasValue() {
            return !!this.storedPhone;
        }
    },
    watch: {
        fd(newFD) {
            if (newFD && this.step === 1 && !newFD.sponsor) {
                this.volunteerType = 'regular';
                this.step = 2;
            }
        },
        step(val) {
            if (val === 6) {
                this.phoneError = null;
                const stored = this.storedPhone;
                if (stored && stored.startsWith('+')) {
                    const known = ['+65', '+60', '+61', '+44', '+91', '+1'];
                    const match = known.find(c => stored.startsWith(c));
                    if (match) {
                        this.phoneCountryCode = match;
                        this.phoneInput = stored.slice(match.length).trim();
                    } else {
                        this.phoneCountryCode = '+65';
                        this.phoneInput = stored;
                    }
                } else {
                    this.phoneCountryCode = '+65';
                    this.phoneInput = stored;
                }
            }
        }
    },
    async mounted() {
        this.isLoggedInAtStart = !!auth.currentUser;
        this.$store.dispatch('fd/listenFDbyID', this.fdId);
        if (auth.currentUser) {
            await this.$store.dispatch('user/loadUserProfile');
            const reg = await this.$store.dispatch('volunteer/getUserRegistration', {
                fdID: this.fdId,
                userID: auth.currentUser.uid
            });
            if (reg) {
                this.location = reg.location;
                this.role = reg.role;
                this.step = 7;
            }
        }
        this.isInitializing = false;
    },
    beforeUnmount() {
        this.$store.dispatch('fd/unsubscribeFD');
    },
    methods: {
        goTo(nextStep) {
            this.stepHistory.push(this.step);
            this.step = nextStep;
        },
        goBack() {
            if (this.stepHistory.length > 0) {
                this.step = this.stepHistory.pop();
            } else {
                this.$router.push('/volunteer');
            }
        },
        selectVolunteerType(type) {
            this.volunteerType = type;
            this.goTo(2);
        },
        selectLocation(loc) {
            this.location = loc;
            if (loc === 'TBR') {
                this.role = 'OTHER';
                this.goTo(4);
            } else {
                this.goTo(3);
            }
        },
        selectRole(r) {
            this.role = r;
            this.goTo(4);
        },
        selectPlusOne(value) {
            this.hasPlusOne = value;
            if (auth.currentUser) {
                this.goTo(6);
            } else {
                this.goTo(5);
            }
        },
        onAuthenticated() {
            this.goTo(6);
        },
        async submitSignUp() {
            // Validate phone
            const localNumber = this.phoneInput.trim();
            if (!localNumber) {
                this.phoneError = 'Please enter your mobile number.';
                return;
            }
            if (!/^[\d\s\-()]{6,15}$/.test(localNumber)) {
                this.phoneError = 'Please enter a valid mobile number.';
                return;
            }
            const phone = this.phoneCountryCode + localNumber;

            this.isLoading = true;
            this.error = null;
            try {
                const existingUser = this.$store.getters['user/getCurrentUser'];
                if (!existingUser || existingUser.id === 'USER_NOT_FOUND') {
                    await this.$store.dispatch('user/loadUserProfile');
                }
                const user = this.$store.getters['user/getCurrentUser'];
                if (!user || user.id === 'USER_NOT_FOUND') {
                    await this.$store.dispatch('user/createUser', {
                        name: (auth.currentUser && auth.currentUser.displayName) || '',
                        email: auth.currentUser.email,
                        phone: ''
                    });
                    await this.$store.dispatch('user/loadUserProfile');
                }

                const freshUser = this.$store.getters['user/getCurrentUser'];

                // Save phone if changed
                if (phone !== (freshUser && freshUser.phone)) {
                    await this.$store.dispatch('user/updateUser', {
                        userID: auth.currentUser.uid,
                        phone
                    });
                }

                // Compute which FD remaining-count fields to update
                const countUpdate = {};
                if (this.location === 'TBR') {
                    const field = this.volunteerType === 'sponsor'
                        ? 'sponsor_tbr_volunteers_remaining'
                        : 'tbr_volunteers_remaining';
                    countUpdate[field] = Math.max(0, this.fd[field] - 1);
                } else if (this.role === 'DRIVER') {
                    const driverField = this.volunteerType === 'sponsor'
                        ? 'sponsor_volunteers_drivers_remaining'
                        : 'volunteers_drivers_remaining';
                    countUpdate[driverField] = Math.max(0, this.fd[driverField] - 1);
                    if (this.hasPlusOne) {
                        const otherField = this.volunteerType === 'sponsor'
                            ? 'sponsor_volunteers_others_remaining'
                            : 'volunteers_others_remaining';
                        countUpdate[otherField] = Math.max(0, this.fd[otherField] - 1);
                    }
                } else {
                    const field = this.volunteerType === 'sponsor'
                        ? 'sponsor_volunteers_others_remaining'
                        : 'volunteers_others_remaining';
                    countUpdate[field] = Math.max(0, this.fd[field] - 1);
                }

                const canBeHelper = false;

                await this.$store.dispatch('volunteer/signUpVolunteer', {
                    fdID: this.fdId,
                    userID: auth.currentUser.uid,
                    name: (freshUser && freshUser.name) || (auth.currentUser && auth.currentUser.displayName) || '',
                    role: this.role,
                    location: this.location,
                    withHelper: this.hasPlusOne,
                    canBeHelper: canBeHelper,
                    volunteerType: this.volunteerType,
                    countUpdate: countUpdate
                });

                this.step = 7;
            } catch (err) {
                this.error = err.message || 'Something went wrong. Please try again.';
            }
            this.isLoading = false;
        },
        formatDayOfWeek(timestamp) {
            if (!timestamp) return '';
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'Asia/Singapore' });
        },
        async shareSignUpLink() {
            const url = `${window.location.origin}${window.location.pathname}#/volunteer/${this.fdId}`;
            const text = `Join me in volunteering at the Apex Food Distribution on ${this.formatDate(this.fd.date)}! Sign up here:`;
            if (navigator.share) {
                try {
                    await navigator.share({ title: 'Volunteer Sign-Up', text, url });
                } catch {
                    // user cancelled
                }
            } else {
                window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank', 'noopener');
            }
        },
        formatDate(timestamp) {
            if (!timestamp) return '';
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                timeZone: 'Asia/Singapore'
            });
        },
        addToCalendar() {
            const fd = this.fd;
            const parts = new Intl.DateTimeFormat('en-CA', {
                timeZone: 'Asia/Singapore',
                year: 'numeric', month: '2-digit', day: '2-digit'
            }).formatToParts(fd.date.toDate());
            const part = (type) => Number(parts.find(p => p.type === type).value);

            const timeStr = this.location === 'TBR' ? this.tbrTime : (fd.time || '13:00');
            const [hour, minute] = timeStr.split(':').map(Number);

            const startUTC = new Date(Date.UTC(part('year'), part('month') - 1, part('day'), hour - 8, minute || 0));
            const endUTC = new Date(startUTC.getTime() + 2.5 * 60 * 60 * 1000);

            const stamp = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

            const ics = [
                'BEGIN:VCALENDAR',
                'VERSION:2.0',
                'PRODID:-//Apex//Food Distribution//EN',
                'CALSCALE:GREGORIAN',
                'BEGIN:VEVENT',
                'UID:fd-' + (fd.id || stamp(startUTC)) + '@apexbt.org',
                'DTSTAMP:' + stamp(new Date()),
                'DTSTART:' + stamp(startUTC),
                'DTEND:' + stamp(endUTC),
                'SUMMARY:Apex Food Distribution (Volunteer)',
                'LOCATION:' + (this.location === 'TBR' ? '32 Telok Blangah Rise' : 'Void Deck\\, 89 Redhill Close'),
                'DESCRIPTION:You signed up to volunteer at the Apex Redhill Food Distribution.',
                'END:VEVENT',
                'END:VCALENDAR'
            ].join('\r\n');

            const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'apex-food-distribution.ics';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }
}
</script>

<style scoped>
/* Stepper */
.wizard-stepper {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 0.5rem 0 1.25rem;
}

.wizard-stepper__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 56px;
}

.wizard-stepper__dot {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #e5e7eb;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: 700;
    transition: background 0.2s, box-shadow 0.2s;
    flex-shrink: 0;
}

.wizard-stepper__item.is-active .wizard-stepper__dot {
    background: #14b8a6;
    color: #fff;
    box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.18);
}

.wizard-stepper__item.is-done .wizard-stepper__dot {
    background: #14b8a6;
    color: #fff;
}

.wizard-stepper__label {
    font-size: 0.65rem;
    color: #9ca3af;
    margin-top: 0.35rem;
    font-weight: 500;
    white-space: nowrap;
}

.wizard-stepper__item.is-active .wizard-stepper__label {
    color: #0f766e;
    font-weight: 700;
}

.wizard-stepper__item.is-done .wizard-stepper__label {
    color: #0f766e;
}

.wizard-stepper__line {
    flex: 1;
    height: 2px;
    background: #e5e7eb;
    margin-top: 13px;
    transition: background 0.2s;
    min-width: 16px;
}

.wizard-stepper__line.is-done {
    background: #14b8a6;
}

.fd-header {
    text-align: center;
    padding: 0.75rem 0 1rem;
}

.fd-header__date {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f172a;
}

.fd-header__sponsor {
    font-size: 0.875rem;
    color: #6366f1;
    font-weight: 600;
    margin-top: 0.2rem;
}

.wizard-step {
    padding: 0.5rem 0;
}

.wizard-step__question {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 1.25rem;
    text-align: center;
}

.wizard-step__question--sm {
    font-size: 0.9rem;
    margin-bottom: 0.65rem;
}

.wizard-choice-btn {
    display: block;
    width: 100%;
    padding: 1rem 1.25rem;
    border-radius: 14px;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    cursor: pointer;
    border: none;
    transition: transform 0.1s ease, box-shadow 0.1s ease;
}

.wizard-choice-btn:active {
    transform: scale(0.98);
}

.role-btn-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.is-choice-full {
    background: #f3f4f6;
    color: #9ca3af;
    border: 1.5px solid #e5e7eb;
    cursor: not-allowed;
    opacity: 0.8;
}

.role-fallback-hint {
    font-size: 0.8rem;
    color: #0f766e;
    margin-top: 0.4rem;
    margin-left: 0.25rem;
}

.choice-full-tag {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: #e5e7eb;
    color: #6b7280;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    flex-shrink: 0;
}

.is-primary-choice {
    background: linear-gradient(135deg, #14b8a6, #0ea5e9);
    color: #fff;
    box-shadow: 0 8px 18px -8px rgba(14, 165, 233, 0.6);
}

.is-secondary-choice {
    background: #f3f4f6;
    color: #374151;
    border: 1.5px solid #e5e7eb;
}

/* Location cards */
.location-card {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 1rem 1.15rem;
    border-radius: 14px;
    border: 1.5px solid #e5e7eb;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.location-card:hover:not(.is-disabled) {
    border-color: #14b8a6;
    box-shadow: 0 6px 16px -8px rgba(20, 184, 166, 0.4);
}

.location-card.is-disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.location-card__icon {
    font-size: 1.4rem;
}

.location-card__body {
    flex: 1;
}

.location-card__name {
    font-weight: 700;
    color: #0f172a;
}

.location-card__detail {
    font-size: 0.85rem;
    color: #6b7280;
}

.location-card__address {
    font-size: 0.78rem;
    color: #9ca3af;
    margin-top: 0.2rem;
}

.location-card__map-link {
    color: #14b8a6;
    font-weight: 600;
    text-decoration: none;
}

.location-card__map-link:hover {
    text-decoration: underline;
}

.driver-option {
    margin-bottom: 1.5rem;
}

.role-info-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.8rem;
    color: #6b7280;
    cursor: pointer;
    margin-top: 0.3rem;
    margin-left: 0.25rem;
    user-select: none;
}

.role-info-toggle:hover {
    color: #374151;
}

.role-info-box {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 10px;
    padding: 0.65rem 0.9rem;
    font-size: 0.85rem;
    color: #065f46;
    margin-top: 0.4rem;
}

.location-card__badge {
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
}

.location-card__badge.is-available {
    background: #d1fae5;
    color: #065f46;
}

.location-card__badge.is-full {
    background: #f3f4f6;
    color: #6b7280;
}

/* Confirm summary */
.confirm-summary {
    border-radius: 12px;
    border: 1.5px solid #e5e7eb;
    padding: 0.6rem 0.9rem !important;
}

.confirm-summary__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.3rem 0;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.875rem;
}

.confirm-summary__row:last-child {
    border-bottom: none;
}

.confirm-summary__label {
    color: #6b7280;
    font-weight: 500;
}

.phone-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.4rem;
}

.phone-addons {
    margin-bottom: 0;
}

.phone-required-tag {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: #fee2e2;
    color: #b91c1c;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
}

.phone-optional-tag {
    font-size: 0.72rem;
    font-weight: 600;
    color: #9ca3af;
}

.tnc-label {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    color: #374151;
    cursor: pointer;
}

.tnc-link {
    color: #0ea5e9;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
}

.tnc-section-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
}

.tnc-body {
    font-size: 0.92rem;
    color: #4b5563;
    line-height: 1.6;
}

/* Success screen */
.success-hero {
    border-radius: 14px;
    padding: 0.9rem 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #fff;
    background: linear-gradient(135deg, #10b981 0%, #14b8a6 55%, #0ea5e9 100%);
    box-shadow: 0 8px 20px -10px rgba(16, 185, 129, 0.65);
    animation: success-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes success-in {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
}

.success-hero__emoji {
    font-size: 1.6rem;
    line-height: 1;
    flex-shrink: 0;
}

.success-hero__title {
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
}

.success-hero__subtitle {
    font-size: 0.82rem;
    opacity: 0.92;
    margin-top: 0.15rem;
}

.success-actions {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin-top: 1rem;
}

.info-card {
    background: #fff;
    border-radius: 16px;
    padding: 1.25rem 1.35rem;
    margin-top: 1rem;
    box-shadow: 0 6px 20px -12px rgba(15, 23, 42, 0.25);
    border: 1px solid #eef0f4;
}

.info-card__rows {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.info-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 1rem;
    color: #374151;
}

.info-row__icon {
    font-size: 1.15rem;
}

.calendar-btn {
    width: 100%;
    height: 2.85rem;
    font-weight: 700;
    color: #fff;
    border: none;
    background: linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%);
    box-shadow: 0 10px 22px -10px rgba(14, 165, 233, 0.7);
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.calendar-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 26px -10px rgba(14, 165, 233, 0.8);
    color: #fff;
}

.share-btn {
    width: 100%;
    height: 2.85rem;
    font-weight: 700;
    color: #0f766e;
    border: 1.5px solid #14b8a6;
    background: #f0fdf4;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.share-btn:hover {
    transform: translateY(-2px);
    background: #ccfbf1;
    color: #0f766e;
}

.contact-btn {
    width: 100%;
    height: 2.85rem;
    font-weight: 700;
    color: #fff;
    border: none;
    background: #25d366;
    box-shadow: 0 8px 18px -8px rgba(37, 211, 102, 0.6);
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.contact-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 22px -8px rgba(37, 211, 102, 0.7);
    color: #fff;
}
</style>
